import React, { useState } from 'react';
import SignaturePad from './SignaturePad';
import AgreementLetterhead from './AgreementLetterhead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Default preview for the public website forms. */
export const publicContractPreview = (product, company, companyNumber) =>
  fetch(`${BACKEND_URL}/api/trials/contracts/${product}/preview?company=${encodeURIComponent(company || '')}${companyNumber ? `&company_number=${encodeURIComponent(companyNumber)}` : ''}`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('The agreement could not be loaded. Please try again.'))));

/** An empty signature, for a form's initial state. */
export const emptySignature = (name = '') => ({ name, title: '', agree: false, image: null, method: 'drawn' });

/** What is still missing before the signature can be sent (empty list when ready). */
export const signatureProblems = (sig) => {
  const out = [];
  if ((sig?.name || '').trim().split(/\s+/).length < 2) out.push('Type your full name (first and last name).');
  if (!sig?.image) out.push(sig?.method === 'typed' ? 'Type your name to create your signature.' : 'Sign in the signature box.');
  if (!sig?.agree) out.push('Tick to confirm you agree and are authorised to sign.');
  return out;
};
export const signatureReady = (sig) => signatureProblems(sig).length === 0;

/**
 * Master Service Agreement: read it, type your name and job title, sign (draw or type), confirm you are authorised.
 * value = { name, title, agree, image, method }: image is the signature as a PNG data URL, method 'drawn' or 'typed'.
 * The agreement HTML comes from our own API, where every value is escaped.
 * `showAgreement={false}` leaves out the "Read the agreement" panel (for pages that show the agreement themselves).
 */
const ContractSign = ({ product, company, companyNumber, value, onChange, preview = publicContractPreview, dark, showAgreement = true }) => {
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const toggle = () => {
    if (!open && !doc) preview(product, company, companyNumber).then(setDoc).catch((e) => setError(e.message));
    setOpen(!open);
  };
  const set = (k, v) => onChange({ ...value, [k]: v });
  const field = 'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:border-[#0a1f2e] focus:outline-none';
  return (
    <div className={`space-y-4 rounded-xl border p-4 ${dark ? 'border-white/20' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold text-[#0a1f2e]">{showAgreement ? 'Master Service Agreement' : 'Your signature'}</div>
        {showAgreement && <button type="button" onClick={toggle} className="text-sm underline text-[#0a1f2e]">{open ? 'Hide the agreement' : 'Read the agreement'}</button>}
      </div>
      {showAgreement && open && (
        <div className="contract-text max-h-80 overflow-y-auto rounded-lg border bg-white p-4 text-xs leading-relaxed text-gray-700 [&_h1]:text-center [&_h1]:text-base [&_h1]:font-bold [&_h2]:mt-3 [&_h2]:font-semibold [&_p]:mt-2 [&_ul]:list-disc [&_ul]:pl-5">
          {error && <p className="text-red-600">{error}</p>}
          {!doc && !error && <p>Loading…</p>}
          {doc && <AgreementLetterhead info={doc.letterhead} compact />}
          {/* eslint-disable-next-line react/no-danger */}
          {doc && <div dangerouslySetInnerHTML={{ __html: doc.html }} />}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm font-medium text-gray-700">Your full name
          <input value={value.name} onChange={(e) => set('name', e.target.value)} autoComplete="name" className={field} /></label>
        <label className="block text-sm font-medium text-gray-700">Job title
          <input value={value.title} onChange={(e) => set('title', e.target.value)} autoComplete="organization-title" placeholder="e.g. Director" className={field} /></label>
      </div>
      <SignaturePad name={value.name} value={value} onChange={(s) => onChange({ ...value, ...s })} />
      <label className="flex items-start gap-3 text-sm text-gray-700">
        <input type="checkbox" className="mt-1" checked={value.agree} onChange={(e) => set('agree', e.target.checked)} />
        <span>I have read and agree to the Master Service Agreement. I confirm that I am authorised to sign it, and to
          place orders under it, on behalf of {company || 'my company'}, and that we will follow the laws and codes of
          conduct that apply to the service. I agree to sign electronically and that my electronic signature has the
          same effect as a handwritten signature.</span>
      </label>
      <p className="text-xs text-gray-500">We record your name, signature, the time, your IP address and browser with the signed copy.
        A PDF copy is emailed to you and to VOIP2.</p>
    </div>
  );
};

export default ContractSign;
